import ExcelJS from "exceljs";
import { STATUS_CLOSED, STATUS_OPEN, STATUS_WIP, STATUS_NREL } from "./excel-constants";
import { R, toJSON } from "./rational";

/**
 * Handles reading data from Excel files
 */
export class ExcelReader {
  constructor() {
    this.workbook = new ExcelJS.Workbook();
  }

  /**
   * Loads data from an Excel file
   */
  async loadFromFile(file) {
    const buffer = await this.readFileAsBuffer(file);
    await this.workbook.xlsx.load(buffer);

    const worksheet = this.workbook.getWorksheet(1);
    const structure = this.analyzeWorksheetStructure(worksheet);
    const data = this.extractData(worksheet, structure);

    return this.buildReturnProps(data, structure);
  }

  /**
   * Reads file as array buffer
   */
  readFileAsBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Analyzes the structure of the worksheet to determine column positions
   */
  analyzeWorksheetStructure(worksheet) {
    let headerRow = null;
    let firstDataRow = null;
    const offset = 3;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 2) {
        headerRow = this.cleanRowValues(row.values);
      } else if (rowNumber === 3) {
        firstDataRow = this.cleanRowValues(row.values);
      }
    });

    const firstStepCol = this.findFirstStatusColumn(firstDataRow);
    const lastStepCol = headerRow.length - 1;
    const firstResponsibleCol = firstStepCol - offset !== 1 ? offset + 1 : 0;
    const lastResponsibleCol = firstResponsibleCol > 0 ? firstStepCol - 1 : 0;

    return {
      headerRow,
      firstStepCol,
      lastStepCol,
      firstResponsibleCol,
      lastResponsibleCol,
      offset,
    };
  }

  /**
   * Removes the empty first element that ExcelJS adds
   */
  cleanRowValues(values) {
    const cleaned = [...values];
    cleaned.shift();
    return cleaned;
  }

  /**
   * Finds the first column containing status values
   */
  findFirstStatusColumn(rowValues) {
    const statusValues = [STATUS_OPEN, STATUS_WIP, STATUS_CLOSED, STATUS_NREL];

    for (let i = 0; i < rowValues.length; i++) {
      if (statusValues.includes(rowValues[i])) {
        return i + 1;
      }
    }

    return -1;
  }

  /**
   * Extracts all data from the worksheet
   */
  extractData(worksheet, structure) {
    const deliverables = [];

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber < 3) return;

      const item = this.extractRowData(row, structure);
      deliverables.push(item);
    });

    return deliverables;
  }

  /**
   * Extracts data from a single row
   */
  extractRowData(row, structure) {
    const item = {
      id: row.getCell(1).value,
      description: row.getCell(2).value,
      weight: toJSON(R(row.getCell(3).value || 0)),
      showEditDialog: false,
    };

    // Add responsible columns
    if (structure.firstResponsibleCol > 0) {
      let counter = 1;
      for (let col = structure.firstResponsibleCol; col <= structure.lastResponsibleCol; col++) {
        const key = counter === 1 ? "responsible" : `responsible${counter}`;
        item[key] = row.getCell(col).value;
        counter++;
      }
    }

    // Add step columns
    let stepCounter = 1;
    for (let col = structure.firstStepCol; col <= structure.lastStepCol; col++) {
      item[`step${stepCounter}`] = row.getCell(col).value;
      stepCounter++;
    }

    // Add fulfillment
    const fulfillmentValue = row.getCell(structure.headerRow.length).value;
    item.fulfillment = typeof fulfillmentValue === "number" ? toJSON(R(fulfillmentValue)) : fulfillmentValue;

    return item;
  }

  /**
   * Builds the return props object with all extracted data
   */
  buildReturnProps(deliverables, structure) {
    const responsibles = this.buildResponsiblesArray(structure);
    const steps = this.buildStepsArray(structure);
    const headers = this.buildHeadersArray(structure, responsibles, steps);

    return {
      deliverables,
      responsibles,
      steps,
      headers,
    };
  }

  /**
   * Builds the responsibles array from structure
   */
  buildResponsiblesArray(structure) {
    const responsibles = [];

    if (structure.firstResponsibleCol === 0) return responsibles;

    let counter = 1;
    for (let i = structure.firstResponsibleCol; i <= structure.lastResponsibleCol; i++) {
      const identity = counter === 1 ? "responsible" : `responsible${counter}`;
      responsibles.push({
        text: structure.headerRow[i - 1],
        identity,
      });
      counter++;
    }

    return responsibles;
  }

  /**
   * Builds the steps array from structure
   */
  buildStepsArray(structure) {
    const steps = [];
    let counter = 1;

    for (let i = structure.firstStepCol; i <= structure.lastStepCol; i++) {
      steps.push({
        text: structure.headerRow[i - 1],
        identity: `step${counter}`,
      });
      counter++;
    }

    return steps;
  }

  /**
   * Builds the headers array for the table
   */
  buildHeadersArray(structure, responsibles, steps) {
    const headers = [
      {
        text: "No",
        align: "start",
        value: "id",
        sortable: true,
        editable: false,
        headerEditable: false,
      },
      { text: "Description", value: "description", editable: true },
      { text: "Weight", value: "weight", editable: true },
    ];

    // Add responsible headers
    responsibles.forEach((responsible, index) => {
      headers.push({
        text: responsible.text,
        value: responsible.identity,
        editable: true,
        sortable: false,
        headerEditable: true,
      });
    });

    // Add step headers
    steps.forEach((step) => {
      headers.push({
        text: step.text,
        value: step.identity,
        editable: true,
        sortable: false,
        headerEditable: true,
      });
    });

    // Add fulfillment header
    headers.push({
      text: "Fulfillment",
      value: "fulfillment",
      editable: false,
    });

    return headers;
  }
}
